
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function About() {
  useEffect(() => {
    // Add SEO meta tags dynamically
    document.title = 'About Us - Central Demolition & Services | Our Mission & Values';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn about Central Demolition & Services - our mission, values, and commitment to safety, respect, and excellence in demolition services across New England.');
    }

    // Add Schema.org JSON-LD for About page
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About Central Demolition & Services",
      "description": "Learn about our mission, values, and commitment to safety and excellence",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/about`,
      "mainEntity": {
        "@type": "Organization",
        "name": "Central Demolition & Services",
        "description": "Professional demolition services with commitment to safety, respect, and cultural consciousness",
        "foundingDate": "2000",
        "email": "contact@central-demolition.com",
        "knowsAbout": [
          "Demolition",
          "Construction Safety",
          "Cultural Heritage Respect",
          "Environmental Responsibility"
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Professional Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Safety Training",
                "description": "Comprehensive safety protocols and training"
              }
            }
          ]
        }
      }
    });
    document.head.appendChild(schemaScript);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-in-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Built on <span className="text-blue-600">Respect</span>.<br />
              Driven by <span className="text-blue-600">Excellence</span>.
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              At Central Demolition & Services, we believe that every project deserves the highest level 
              of professionalism, safety, and cultural awareness. Our mission is rooted in respect—for our 
              clients, our communities, and the heritage that shapes our work.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-on-scroll">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-lg text-gray-600 mb-6">
                We provide high-quality demolition and general labor services with unwavering commitment 
                to honesty, cultural consciousness, and professional excellence. Every project we undertake 
                is guided by our core values of safety, respect, and precision.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our team understands the importance of cultural heritage and community values, including 
                our deep respect for Native American traditions and the land we work on. This awareness 
                shapes how we approach every project with mindfulness and responsibility.
              </p>
              <p className="text-lg text-gray-600">
                From residential homes to complex laboratory facilities, we bring the same level of 
                dedication and expertise to every job, ensuring safety, efficiency, and respect 
                throughout the entire process.
              </p>
            </div>
            <div className="fade-in-on-scroll">
              <img 
                src="/images/4a0e12e855a25ace4780b5c84741ebc4.jpg"
                alt="Our Professional Team"
                className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in-on-scroll">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These principles guide every decision we make and every project we complete.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center bg-white p-8 rounded-lg shadow-lg fade-in-on-scroll hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-fill text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Safety First</h3>
              <p className="text-gray-600">
                Rigorous safety protocols and continuous training ensure the well-being of our team and clients.
              </p>
            </div>

            <div className="text-center bg-white p-8 rounded-lg shadow-lg fade-in-on-scroll hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-heart-fill text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Cultural Respect</h3>
              <p className="text-gray-600">
                Deep appreciation for Native American heritage and community values guides our approach.
              </p>
            </div>

            <div className="text-center bg-white p-8 rounded-lg shadow-lg fade-in-on-scroll hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-award-fill text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Excellence</h3>
              <p className="text-gray-600">
                Commitment to the highest standards of workmanship and professional service delivery.
              </p>
            </div>

            <div className="text-center bg-white p-8 rounded-lg shadow-lg fade-in-on-scroll hover:shadow-xl transition-shadow duration-300">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-handshake-fill text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold mb-4">Integrity</h3>
              <p className="text-gray-600">
                Honest communication, transparent pricing, and reliable service in every client relationship.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Factors */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in-on-scroll">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why Clients Trust Us
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our reputation is built on decades of successful projects and satisfied clients 
              throughout New England.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-blue-50 p-8 rounded-lg fade-in-on-scroll">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-check-line text-white font-bold"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Fully Licensed & Insured</h3>
              </div>
              <p className="text-gray-700">
                Complete licensing across all New England states with comprehensive liability 
                and workers' compensation insurance coverage.
              </p>
            </div>

            <div className="bg-blue-50 p-8 rounded-lg fade-in-on-scroll">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-check-line text-white font-bold"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Decades of Experience</h3>
              </div>
              <p className="text-gray-700">
                Our team brings combined decades of specialized experience in controlled 
                demolition and complex project management.
              </p>
            </div>

            <div className="bg-blue-50 p-8 rounded-lg fade-in-on-scroll">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-check-line text-white font-bold"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Safety &amp; Respect</h3>
              </div>
              <p className="text-gray-700">
                Unwavering commitment to safety protocols and cultural consciousness 
                in every aspect of our work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in-on-scroll">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Expert Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Licensed, insured, and trained professionals dedicated to delivering 
              exceptional results on every project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="fade-in-on-scroll">
              <img 
                src="/images/efc6c3071bf3f3fdb5e05a15d12f3bf3.jpg"
                alt="Team at Work"
                className="w-full h-80 object-cover object-top rounded-lg shadow-lg"
              />
            </div>
            <div className="fade-in-on-scroll">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Qualified &amp; Certified Professionals
              </h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <i className="ri-award-line text-blue-600 text-xl mt-1 mr-3"></i>
                  <div>
                    <h4 className="font-semibold text-lg">Licensed Demolition Specialists</h4>
                    <p className="text-gray-600">Certified in controlled demolition and hazardous material handling</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-shield-check-line text-blue-600 text-xl mt-1 mr-3"></i>
                  <div>
                    <h4 className="font-semibold text-lg">Safety-Trained Personnel</h4>
                    <p className="text-gray-600">OSHA-certified with ongoing safety training and protocols</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-tools-line text-blue-600 text-xl mt-1 mr-3"></i>
                  <div>
                    <h4 className="font-semibold text-lg">Equipment Specialists</h4>
                    <p className="text-gray-600">Expert operators of modern demolition and construction equipment</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <i className="ri-customer-service-line text-blue-600 text-xl mt-1 mr-3"></i>
                  <div>
                    <h4 className="font-semibold text-lg">Project Managers</h4>
                    <p className="text-gray-600">Experienced in coordinating complex multi-phase demolition projects</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="fade-in-on-scroll">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Experience the Difference
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Ready to work with a team that values integrity, safety, and respect? 
              Contact us today for your free consultation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/contact">
                <Button size="lg" className="text-xl px-10 py-5">
                  Get Your Free Consultation
                </Button>
              </Link>
              <a href="tel:857-777-4300" className="text-2xl hover:text-blue-400 transition-colors cursor-pointer">
                <i className="ri-phone-fill mr-2"></i>
                857-777-4300
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Removido o script Neurolead duplicado - já está no index.html */}
    </div>
  );
}
